// @flow

import { PARTICIPANT_ROLE } from '../participants/constants';

/**
 * The capabilities a participant can hold. Features gate on these rather than on
 * a role directly, so that adding a role does not mean auditing every call site.
 *
 * The names of the capabilities that also exist server-side (in prosody's
 * jitsi_default_permissions) are spelled the same on purpose, so that if we later
 * start consuming the server's <permissions> presence element we can merge the two
 * without a translation table.
 *
 * @enum {string}
 */
export const PERMISSIONS = {
    /**
     * Publish audio/video (i.e. be "on stage"). Visitors do not have this.
     */
    SEND_MEDIA: 'send-media',

    SHARE_SCREEN: 'share-screen',
    SEND_GROUPCHAT: 'send-groupchat',
    CREATE_POLLS: 'create-polls',
    RECORDING: 'recording',
    LIVESTREAMING: 'livestreaming',
    TRANSCRIPTION: 'transcription',
    OUTBOUND_CALL: 'outbound-call',
    FLIP: 'flip',

    /**
     * Mute, kick and otherwise act on other participants.
     */
    MODERATE_PARTICIPANTS: 'moderate-participants',

    /**
     * Grant or revoke roles (moderator, panelist).
     */
    GRANT_ROLES: 'grant-roles',

    /**
     * Change room-level settings: lobby, security, webinar configuration.
     */
    MANAGE_ROOM: 'manage-room'
};

/**
 * A visitor is not a MUC role, so it has no PARTICIPANT_ROLE of its own; it is a
 * separate axis (iAmVisitor). It gets an entry in the matrix below anyway, keyed
 * by this pseudo-role, because from a permissions point of view it behaves like
 * the bottom of the role hierarchy.
 *
 * Effective hierarchy: moderator > panelist > participant > visitor.
 */
export const VISITOR_ROLE = 'visitor';

const PARTICIPANT_PERMISSIONS = [
    PERMISSIONS.SEND_MEDIA,
    PERMISSIONS.SHARE_SCREEN,
    PERMISSIONS.SEND_GROUPCHAT,
    PERMISSIONS.FLIP
];

/**
 * A panelist is an elevated participant: they are "on stage" and may drive the
 * session (polls), but they cannot moderate anyone or change the room.
 */
const PANELIST_PERMISSIONS = [
    ...PARTICIPANT_PERMISSIONS,
    PERMISSIONS.CREATE_POLLS
];

/**
 * A presenter is share-only: they drive content (screen share, whiteboard,
 * livepad, YouTube) but their microphone and camera stay locked, so they never
 * publish media of their own.
 */
const PRESENTER_PERMISSIONS = [
    PERMISSIONS.SHARE_SCREEN,
    PERMISSIONS.SEND_GROUPCHAT,
    PERMISSIONS.FLIP
];

const MODERATOR_PERMISSIONS = Object.values(PERMISSIONS);

/**
 * Every capability, in display order. What the permissions indicator in the
 * participants pane walks over.
 */
export const PERMISSION_LIST = MODERATOR_PERMISSIONS;

/**
 * A visitor is view-only: they may follow along and talk in the visitor chat, but
 * they cannot publish media. Promotion is what gets them out of this set.
 */
const VISITOR_PERMISSIONS = [
    PERMISSIONS.SEND_GROUPCHAT
];

/**
 * Role to capability mapping.
 *
 * PARTICIPANT_ROLE.NONE maps to the participant set, not to an empty one: this
 * fork reports the local user's role as 'none' whenever their MUC affiliation is
 * not 'owner' (see ChatRoom's self-presence handling), so 'none' is the ordinary
 * case for a regular attendee rather than a degraded one.
 */
export const ROLE_PERMISSIONS = {
    [PARTICIPANT_ROLE.MODERATOR]: MODERATOR_PERMISSIONS,
    [PARTICIPANT_ROLE.PANELIST]: PANELIST_PERMISSIONS,
    [PARTICIPANT_ROLE.PARTICIPANT]: PARTICIPANT_PERMISSIONS,
    [PARTICIPANT_ROLE.PRESENTER]: PRESENTER_PERMISSIONS,
    [PARTICIPANT_ROLE.NONE]: PARTICIPANT_PERMISSIONS,
    [VISITOR_ROLE]: VISITOR_PERMISSIONS
};
